// utils/payload.ts
import { CAYMAN_REGION } from '@/constants/tenant-configs/cayman';

import { Tenant } from '@/types/common';
import { AddressForm } from '@/types/form';
import { CountryISO } from '@/types/places';
import {
  AddressDetails,
  AddressType,
  MailingAddressPayload,
  User,
  UserAddress,
} from '@/types/user';

import { isCaymanTenant } from './tenant';

interface BuildAddressFormPayloadOptions {
  shouldShowUsTin: boolean;
  shouldShowLocalTin: boolean;
  otherCountriesTin: string[];
  localISO: string;
  shouldAddMailingAddress?: boolean;
}

type TinNormalizationOptions = Pick<
  BuildAddressFormPayloadOptions,
  'shouldShowUsTin' | 'shouldShowLocalTin' | 'otherCountriesTin' | 'localISO'
>;

type MailingNormalizationOptions = Pick<
  BuildAddressFormPayloadOptions,
  'shouldAddMailingAddress'
>;

export function getUserAddressByType(
  user: User | undefined,
  type: AddressType,
): UserAddress | undefined {
  return user?.addresses?.find(a => a.type === type);
}

export function getMailingAddressFromUser(
  user?: User,
): UserAddress | undefined {
  return getUserAddressByType(user, AddressType.MAILING);
}

export function getPermanentAddressFromUser(
  user?: User,
): UserAddress | AddressDetails | undefined {
  return (
    getUserAddressByType(user, AddressType.PERMANENT) ?? user?.addressDetailed
  );
}

function buildAddressDetailedForPayload(
  rawPayload: AddressForm,
  tenant: Tenant,
) {
  const { line1, city, state, zip, country, residence, poBox, poBoxDistrict } =
    rawPayload;

  return {
    line1: typeof line1 === 'string' ? line1 : (line1?.line1 ?? ''),
    city,
    state,
    zip,
    country,
    residence: residence || undefined,
    ...(isCaymanTenant(tenant) && { poBox, poBoxDistrict }),
  };
}

export function getNormalizedTin(
  rawPayload: AddressForm,
  options: TinNormalizationOptions,
): Record<string, string> {
  const { usTin, localTin, otherTins } = rawPayload;
  const { shouldShowUsTin, shouldShowLocalTin, otherCountriesTin, localISO } =
    options;

  const tin: Record<string, string> = {};

  if (usTin && shouldShowUsTin) {
    tin[CountryISO.US] = usTin;
  }

  if (localTin && shouldShowLocalTin) {
    tin[localISO] = localTin;
  }

  if (otherCountriesTin?.length && otherTins) {
    for (const code of otherCountriesTin) {
      if (otherTins[code]) {
        tin[code] = otherTins[code];
      }
    }
  }

  return tin;
}

export function getNormalizedMailingAddress(
  rawPayload: AddressForm,
  tenant: Tenant,
  options: MailingNormalizationOptions,
): MailingAddressPayload | undefined {
  const { shouldAddMailingAddress } = options;

  if (!shouldAddMailingAddress) {
    return undefined;
  }

  const {
    mailingSameAsResidential,
    mailingLine1,
    mailingCity,
    mailingState,
    mailingZip,
    mailingCountry,
    residence,
  } = rawPayload;

  const addressDetailed = buildAddressDetailedForPayload(rawPayload, tenant);

  if (mailingSameAsResidential === false) {
    return {
      line1:
        typeof mailingLine1 === 'string'
          ? mailingLine1
          : (mailingLine1?.line1 ?? ''),
      city: mailingCity,
      state: mailingState || undefined,
      zip: mailingZip || undefined,
      country: mailingCountry,
      ...(residence ? { residence } : {}),
    };
  }

  return { ...addressDetailed };
}

export function getNormalizedTaxResidences(rawPayload: AddressForm) {
  const { differentTaxResidency, taxResidences, country } = rawPayload;
  let normalizedTaxResidences: string = '';

  if (differentTaxResidency) {
    if (Array.isArray(taxResidences)) {
      normalizedTaxResidences = taxResidences.join(',');
    } else if (taxResidences) {
      normalizedTaxResidences = String(taxResidences);
    }
  } else {
    normalizedTaxResidences = country;
  }
  return normalizedTaxResidences;
}

export function buildAddressFormPayload(
  rawPayload: AddressForm,
  tenant: Tenant,
  options: BuildAddressFormPayloadOptions,
) {
  const {
    differentTaxResidency,
    line1,
    state,
    city,
    zip,
    usTin,
    localTin,
    otherTins,
    country,
    residence,
    poBox,
    poBoxDistrict,
    taxResidences,
    mailingSameAsResidential,
    mailingLine1,
    mailingCity,
    mailingState,
    mailingZip,
    mailingCountry,
    ...rest
  } = rawPayload;

  const tin = getNormalizedTin(rawPayload, options);
  const addressDetailed = buildAddressDetailedForPayload(rawPayload, tenant);
  const mailingAddress = getNormalizedMailingAddress(
    rawPayload,
    tenant,
    options,
  );
  const normalizedTaxResidences = getNormalizedTaxResidences(rawPayload);

  const payload = {
    ...rest,
    taxResidences: normalizedTaxResidences,
    addressDetailed,
    ...(mailingAddress !== undefined && { mailingAddress }),
    tin,
  };

  return payload;
}

export function isAddressDetailPopulated(
  addr?: Partial<AddressDetails> | null,
): boolean {
  return Boolean(addr?.line1?.trim() && addr?.country);
}

export function getInitialMailingSameAsResidential(user: User): boolean {
  return !isAddressDetailPopulated(getMailingAddressFromUser(user));
}

export const getTenantDefaultValues = (
  tenant: Tenant,
  user: User,
): Partial<AddressForm> => {
  if (isCaymanTenant(tenant)) {
    return {
      poBox: user?.addressDetailed?.poBox ?? '',
      poBoxDistrict: user?.addressDetailed?.poBoxDistrict ?? '',
      state: user?.addressDetailed?.state ?? CAYMAN_REGION.GRAND_CAYMAN,
    };
  }
  const permanent = getPermanentAddressFromUser(user);
  const mailing = getMailingAddressFromUser(user);
  return {
    state: permanent?.state ?? '',
    mailingSameAsResidential: getInitialMailingSameAsResidential(user),
    mailingLine1: mailing?.line1 ?? '',
    mailingCity: mailing?.city ?? '',
    mailingState: mailing?.state ?? '',
    mailingZip: mailing?.zip ?? '',
    mailingCountry: mailing?.country ?? '',
    residenceCountry: user.residenceCountry ?? '',
  };
};
